// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// Emergent-managed Google Auth provider for Coachly.
// - Verifies session via GET /api/auth/me (httpOnly cookie).
// - `signIn(returnTo)` redirects the browser to Emergent's Google login and
//   comes back to `<origin>/auth/callback#session_id=…` which is picked up by
//   <AuthCallback/> synchronously during render.
// - Seeds legacy `coachly_client_email` + `coachly_client_name` in localStorage
//   so favorites/messages/booking prefill work with zero refactor elsewhere.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Always send cookies for auth on same-origin ingress.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachStatus, setCoachStatus] = useState(null); // { is_coach, slug, is_approved, payments_ready }
  const [modal, setModal] = useState({ open: false, reason: "" });

  const syncLegacyIdentity = (u) => {
    if (u?.email) localStorage.setItem("coachly_client_email", u.email.toLowerCase());
    if (u?.name) localStorage.setItem("coachly_client_name", u.name);
  };

  const clearLegacyIdentity = () => {
    localStorage.removeItem("coachly_client_email");
    localStorage.removeItem("coachly_client_name");
  };

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
      syncLegacyIdentity(r.data);
      // Fire and forget — coach status is used only to gate a nav link.
      axios.get(`${API}/coach/me`)
        .then((cr) => setCoachStatus(cr.data))
        .catch(() => setCoachStatus({ is_coach: false }));
    } catch {
      setUser(null);
      setCoachStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: When returning from Emergent Google Auth the URL fragment
    // carries `#session_id=…` — let <AuthCallback/> exchange it first, don't
    // race the /me check.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signIn = useCallback((returnTo) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const path = returnTo || (window.location.pathname + window.location.search);
    const redirectUrl = window.location.origin + "/auth/callback?next=" + encodeURIComponent(path);
    window.location.href = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  }, []);

  const signOut = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    clearLegacyIdentity();
    setUser(null);
    setCoachStatus(null);
  }, []);

  const openSignIn = useCallback((reason = "") => setModal({ open: true, reason }), []);
  const closeSignIn = useCallback(() => setModal({ open: false, reason: "" }), []);

  const requireAuth = useCallback((onOk, reason = "") => {
    if (user) return onOk?.();
    openSignIn(reason);
    return null;
  }, [user, openSignIn]);

  const value = useMemo(
    () => ({ user, loading, coachStatus, signIn, signOut, refresh, requireAuth, modal, openSignIn, closeSignIn, setUser: (u) => { setUser(u); syncLegacyIdentity(u); } }),
    [user, loading, coachStatus, signIn, signOut, refresh, requireAuth, modal, openSignIn, closeSignIn]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
